import { getUserType } from "util/auth";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import MenuPriceSetting from "components/menu/MenuPriceSetting";
import { styled } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { formatCurrency } from "utils";
import { ProcFee } from "features/mst_proc_fee/types/proc-fee";
import useProcFeeList from "features/mst_proc_fee/hooks/useProcFeeSetting";
import TextFieldComponent from "components/input/TextFieldComponent";

const headerFieldMap: Record<string, string> = {
  交換工賃: "replac_labor_cost",
  処分費: "waste_tire_cost",
  エアバルブ: "rubber_stem_valve_cost",
  窒素充填: "nitrogen_inflation_cost",
  ハブ防錆: "hub_rust_prevention_cost",
};

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  fontSize: 16,
  fontWeight: "bold",
  height: "70px",
  [theme.breakpoints.up("md")]: { fontSize: 18 },
  [theme.breakpoints.up("lg")]: { fontSize: 20 },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  border: "1px solid white",
  textAlign: "center",
  fontWeight: "bold",
  fontSize: 16,
  backgroundColor: "var(--primary-500)",
  width: "16.666%",
  height: "24px",
  [theme.breakpoints.up("md")]: { fontSize: 18 },
  [theme.breakpoints.up("lg")]: { fontSize: 20 },
}));

const StyledBodyTableCell = styled(TableCell)(({ theme }) => ({
  textAlign: "center",
  fontSize: 16,
  width: "210px",
  [theme.breakpoints.up("md")]: { fontSize: 18 },
  [theme.breakpoints.up("lg")]: { fontSize: 20 },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(even)": {
    backgroundColor: "var(--offwhite-300)",
  },
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--white)",
  },
}));

/**
 * Component for setting processing fees.
 */
function ProcFeeSetting() {
  const [isHasError, setIsHasError] = useState(false);
  const headers = ["交換工賃", "処分費", "エアバルブ", "窒素充填", "ハブ防錆"];
  const rimDiameters = [12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24];
  const [editCell, setEditCell] = useState<{
    rim: number;
    header: string;
  } | null>(null);
  const [value, setValue] = useState<number | null>(null);

  const navigate = useNavigate();
  const { setProcFees, procFees, updateProcFeeItem, originalProcFees, setOriginalProcFees } =
    useProcFeeList();

  const [editElementCell, setEditElementCell] = useState<HTMLElement | null>(null);

  const saveFieldData = async (newValue: any, rim: number, field: string) => {
    setProcFees(
      (prevData) =>
        prevData && {
          ...prevData,
          [rim]: {
            ...prevData[rim],
            [field]: newValue,
          },
        },
    );
  };

  const saveOriginalFieldData = async (newValue: number | string, rim: number, field: string) => {
    setOriginalProcFees(
      (prevData) =>
        prevData && {
          ...prevData,
          [rim]: {
            ...prevData[rim],
            [field]: newValue,
          },
        },
    );
  };

  const handleEditCell = (e: React.MouseEvent, rim: number, header: string) => {
    if (isHasError) return;
    setEditCell({ rim, header });
    setEditElementCell(e.currentTarget as HTMLElement);
    const procFee = procFees && procFees[rim];
    if (procFee) {
      const field = headerFieldMap[header];
      if (field) {
        const fieldValue = procFee[field as keyof typeof procFee];
        setValue(Number(fieldValue));
      }
    }
  };

  const validateValue = (num: string): boolean => {
    const LIMIT = 2147483647;
    const numericValue = Number(num);

    return !(numericValue < 0 || num.length > 9 || numericValue > LIMIT);
  };

  const handleChange = (inputValue: string, rim: number, header: string) => {
    setValue(Number(inputValue));
    saveFieldData(inputValue, rim, header);
  };

  useEffect(() => {
    const handleMouseDownOutside = async (event: MouseEvent) => {
      if (isHasError) return;
      if (editCell && editElementCell && value !== null) {
        const eventTarget = event.target as HTMLElement;
        if (eventTarget !== editElementCell && !editElementCell.contains(eventTarget)) {
          const field = headerFieldMap[editCell.header];
          if (field) {
            if (!validateValue(value.toString())) {
              setValue(Number(procFees?.[editCell.rim]?.[field as keyof ProcFee] ?? 0));
              setEditCell(null);
              setEditElementCell(null);
              return;
            }

            if (
              value !== originalProcFees?.[editCell.rim]?.[field as keyof ProcFee] ||
              value === 0
            ) {
              const currentFieldValue = procFees?.[editCell.rim]?.[field as keyof ProcFee];
              const isCleared =
                currentFieldValue === "" ||
                currentFieldValue === null ||
                currentFieldValue === undefined;
              saveFieldData(isCleared ? 0 : value, editCell.rim, field);
              saveOriginalFieldData(isCleared ? 0 : value, editCell.rim, field);
              setEditCell(null);
              setEditElementCell(null);
              await updateProcFeeItem(editCell.rim, field as never, value, isCleared);
            } else {
              setEditCell(null);
              setEditElementCell(null);
            }
          }
        }
      }
    };

    document.addEventListener("mousedown", handleMouseDownOutside);

    return () => {
      document.removeEventListener("mousedown", handleMouseDownOutside);
    };
    // procFees / originalProcFees must be in the deps so that after the user clears a cell
    // (e.g. 0 → "") the mousedown handler reads the freshly-written "" instead of the stale
    // value captured at the time editCell was set. Without this, isCleared stays false and
    // the column_delete_flag update never fires.
  }, [editCell, value, procFees, originalProcFees]);

  return (
    <>
      <Box sx={{ px: { xs: 2, md: 2.5, lg: 5 }, pt: { xs: 2, md: 2.5, lg: 3 } }}>
        <Button onClick={() => navigate("/")} variant="custom-outlined">
          <Box>＜ 戻る</Box>
        </Button>
      </Box>
      <Box
        sx={{ display: { xl: "flex", xs: "block" }, p: { xs: 2, md: 2.5, lg: 5 }, flexWrap: "wrap" }}
      >
      <Box
        sx={{
          flexBasis: { xs: "100%", xl: "290px" },
          display: { xs: "flex", xl: "block" },
          justifyContent: "space-between",
          pb: { xs: 5, xl: 0 },
          mb: { xs: 0, xl: 5 },
        }}
      >
        <Typography
          variant="h5"
          margin={0}
          textAlign="left"
          fontSize={{ xs: 24, md: 28, lg: 33 }}
          marginBottom="50px"
          color="var(--gray-700)"
        >
          店舗設定 <br />
          工賃設定
        </Typography>
        <MenuPriceSetting />
      </Box>
      <Box
        sx={{ margin: "0 auto", display: "flex", alignItems: "center", flexDirection: "column" }}
      >
        <Box
          sx={{
            textAlign: "right",
            mb: 1,
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              mt: { xl: "100px" },
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "var(--black)",
                fontSize: { xs: 16, md: 18, lg: 20 },
                fontWeight: "bold",
                marginRight: "20px",
              }}
            >
              {getUserType() === "C0002" ? "黒字：本部/企業設定" : "黒字：本部設定"}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "var(--red-800)",
                fontSize: { xs: 16, md: 18, lg: 20 },
                fontWeight: "bold",
                marginRight: "15px",
              }}
            >
              {getUserType() === "C0006" ? "赤字：企業上書設定" : "赤字：店舗上書設定"}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "var(--black)",
                fontSize: { xs: 16, md: 18, lg: 20 },
                fontWeight: "bold",
              }}
            >
              (工賃を設定します（1本税込）)
            </Typography>
          </Box>
          <Typography
            variant="h6"
            sx={{
              color: "var(--black)",
              fontSize: { xs: 16, md: 18, lg: 20 },
              fontWeight: "bold",
            }}
          >
            金額を「空欄」で更新した場合、本部設定価格に戻ります。
          </Typography>
        </Box>
        <Box sx={{ overflowX: "auto", width: "100%" }}>
          <Table sx={{ minWidth: 1225, width: "100%" }}>
            <StyledTableHead>
              <TableRow>
                <StyledTableCell
                  sx={{
                    border: "none",
                    backgroundColor: "var(--white)",
                    fontSize: { xs: 24, md: 28, lg: 33 },
                    fontWeight: { xs: "bold", lg: "medium" },
                    color: "var(--gray-700)",
                  }}
                >
                  工賃設定
                </StyledTableCell>
                {headers.map((header) => (
                  <StyledTableCell key={header}>{header}</StyledTableCell>
                ))}
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {rimDiameters.map((rim) => (
                <StyledTableRow key={rim}>
                  <StyledTableCell>{rim}インチ</StyledTableCell>
                  {headers.map((header) => {
                    const procFee = procFees && procFees[rim];
                    if (procFee) {
                      const field = headerFieldMap[header] as keyof ProcFee;
                      if (field) {
                        const isEditing = editCell?.rim === rim && editCell.header === header;

                        return (
                          <StyledBodyTableCell
                            key={header}
                            onClick={(e) => handleEditCell(e, rim, header)}
                            sx={{
                              padding: "10px 16px",
                              color: procFee[`store_login_honbu_flag_${field}` as keyof ProcFee]
                                ? "#000000"
                                : "#ff0000",
                            }}
                          >
                            {isEditing ? (
                              <TextFieldComponent
                                type="string"
                                value={procFee[field]}
                                normalizeHalfWidth
                                onInputText={(e) => {
                                  const sanitizedValue = e.target.value.replace(/[^0-9]/g, "");
                                  if (sanitizedValue.length > 9) {
                                    setIsHasError(true);
                                  } else {
                                    setIsHasError(false);
                                  }
                                  handleChange(sanitizedValue, rim, field);
                                }}
                                sxRoot={{
                                  width: "100%",
                                }}
                                inputProps={{ autoFocus: true }}
                                isHiddenError
                                onError={(error) => setIsHasError(error)}
                              />
                            ) : procFee[field] != null ? (
                              formatCurrency(procFee[field] as number)
                            ) : (
                              ""
                            )}
                          </StyledBodyTableCell>
                        );
                      }
                    }
                    return <StyledBodyTableCell key={header} />;
                  })}
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Box>
    </>
  );
}

export default ProcFeeSetting;
